import { useEffect, useRef } from "react";
import { TransactionWithDate } from "../../../utils/transactionProcessing";
import { TransactionCategory, TransactionCategoryGroup } from "../../../types";

interface UseAutoFilterSelectionProps {
  typeFilter: string;
  groupFilter: string;
  categoryFilter: string;
  transactions: TransactionWithDate[];
  expenseCategories: TransactionCategory[];
  incomeCategories: TransactionCategory[];
  categoryGroups: TransactionCategoryGroup[];
  isFullyLoaded: boolean;
  setTypeFilter: (value: string) => void;
  setGroupFilter: (value: string) => void;
  setCategoryFilter: (value: string) => void;
  typeFilterManuallyChanged: React.MutableRefObject<boolean>;
  groupFilterManuallyChanged: React.MutableRefObject<boolean>;
  categoryFilterManuallyChanged: React.MutableRefObject<boolean>;
}

/**
 * Hook to handle auto-selection of filters when related filters change.
 * Auto-selects group when type is selected, category when group is selected, etc.
 */
export const useAutoFilterSelection = ({
  typeFilter,
  groupFilter,
  categoryFilter,
  transactions,
  expenseCategories,
  incomeCategories,
  categoryGroups,
  isFullyLoaded,
  setTypeFilter,
  setGroupFilter,
  setCategoryFilter,
  typeFilterManuallyChanged,
  groupFilterManuallyChanged,
  categoryFilterManuallyChanged,
}: UseAutoFilterSelectionProps): {
  handleTypeFilterChange: (newType: string) => void;
  handleGroupFilterChange: (newGroup: string) => void;
  handleCategoryFilterChange: (newCategory: string) => void;
} => {
  // Reset type filter if "Transfer" or "Adjustment" is selected when group or category is selected
  // (since Transfer and Adjustment don't have groups/categories)
  useEffect(() => {
    if ((groupFilter !== "All" || categoryFilter !== "All") && 
        (typeFilter === "Transfer" || typeFilter === "Adjustment")) {
      setTypeFilter("All");
    }
  }, [groupFilter, categoryFilter, typeFilter, setTypeFilter]);

  // Handler functions that auto-select related filters when user makes a selection
  // Auto-select only happens AFTER user manually selects a filter, not on initial load
  const handleTypeFilterChange = (newType: string): void => {
    typeFilterManuallyChanged.current = true;
    setTypeFilter(newType);
  };
  
  const handleGroupFilterChange = (newGroup: string): void => {
    groupFilterManuallyChanged.current = true;
    setGroupFilter(newGroup);
    // If selecting "All" for group, also reset category to "All" to show all categories
    if (newGroup === "All") {
      categoryFilterManuallyChanged.current = true; // Mark as manually changed so auto-selection doesn't interfere
      setCategoryFilter("All");
    } else {
      // If selecting a specific group, check if current category belongs to that group
      // If not, reset category to "All" so only categories from the selected group are shown
      if (categoryFilter !== "All") {
        const selectedGroup = categoryGroups.find((g) => g.name === newGroup);
        if (selectedGroup) {
          const currentCategory = expenseCategories.find((cat) => cat.name === categoryFilter) ||
                                  incomeCategories.find((cat) => cat.name === categoryFilter);
          // If current category doesn't belong to the new group, reset it
          if (!currentCategory || currentCategory.groupId !== selectedGroup.id) {
            categoryFilterManuallyChanged.current = true; // Mark as manually changed so auto-selection doesn't interfere
            setCategoryFilter("All");
          }
        }
      }
    }
  };
  
  const handleCategoryFilterChange = (newCategory: string): void => {
    categoryFilterManuallyChanged.current = true;
    setCategoryFilter(newCategory);
    // If selecting "All" for category, reset group to "All" to show all groups
    if (newCategory === "All") {
      groupFilterManuallyChanged.current = true; // Mark as manually changed so auto-selection doesn't interfere
      setGroupFilter("All");
    }
  };
  
  // Auto-select group when type is selected and only one group is available
  // Only runs if user manually changed the type filter AND data is fully loaded
  useEffect(() => {
    if (!isFullyLoaded) return; // Don't auto-select until data is fully loaded
    if (typeFilterManuallyChanged.current && 
        typeFilter !== "All" && 
        typeFilter !== "Transfer" && 
        typeFilter !== "Adjustment" && 
        groupFilter === "All") {
      const groupIdSet = new Set<string>();
      transactions.forEach((transaction) => {
        if (typeFilter === "Income" && transaction.type !== "income") return;
        if (typeFilter === "Expenses" && transaction.type !== "expense") return;
        if (transaction.type !== "transfer" && transaction.type !== "adjustment" && transaction.category) {
          const transactionCategory = expenseCategories.find((cat) => cat.name === transaction.category) ||
                                      incomeCategories.find((cat) => cat.name === transaction.category);
          if (transactionCategory && transactionCategory.groupId) {
            groupIdSet.add(transactionCategory.groupId);
          }
        }
      });
      const groupsForType = categoryGroups.filter((group) => groupIdSet.has(group.id));
      if (groupsForType.length === 1) {
        setGroupFilter(groupsForType[0].name);
      }
    }
  }, [typeFilter, groupFilter, transactions, expenseCategories, incomeCategories, categoryGroups, isFullyLoaded, setGroupFilter]);
  
  // Auto-select category when group is selected and only one category is available
  // Only runs if user manually changed the group filter AND data is fully loaded
  useEffect(() => {
    if (!isFullyLoaded) return; // Don't auto-select until data is fully loaded
    if (groupFilterManuallyChanged.current && 
        groupFilter !== "All" && 
        categoryFilter === "All") {
      const selectedGroup = categoryGroups.find((g) => g.name === groupFilter);
      if (selectedGroup) {
        const categoryNameSet = new Set<string>();
        transactions.forEach((transaction) => {
          // Apply type filter if set
          if (typeFilter !== "All") {
            if (typeFilter === "Transfer" && transaction.type !== "transfer") return;
            if (typeFilter === "Income" && transaction.type !== "income") return;
            if (typeFilter === "Expenses" && transaction.type !== "expense") return;
            if (typeFilter === "Adjustment" && transaction.type !== "adjustment") return;
          }
          
          // Only process expenses/income
          if (transaction.type !== "transfer" && transaction.type !== "adjustment" && transaction.category) {
            const transactionCategory = expenseCategories.find((cat) => cat.name === transaction.category) ||
                                        incomeCategories.find((cat) => cat.name === transaction.category);
            if (transactionCategory && transactionCategory.groupId === selectedGroup.id) {
              categoryNameSet.add(transaction.category);
            }
          }
        });
        const categoriesForGroup = Array.from(categoryNameSet).sort();
        if (categoriesForGroup.length === 1) {
          setCategoryFilter(categoriesForGroup[0]);
        }
      }
    }
  }, [groupFilter, categoryFilter, typeFilter, transactions, expenseCategories, incomeCategories, categoryGroups, isFullyLoaded, setCategoryFilter]);
  
  // Reset category if it doesn't belong to the selected group (when group changes externally)
  // This handles cases where group is set programmatically (e.g., from initialFilters)
  // Only reset if group was changed manually (not when category was changed, which auto-selects group)
  useEffect(() => {
    if (!isFullyLoaded) return; // Don't auto-select until data is fully loaded
    if (groupFilterManuallyChanged.current && 
        groupFilter !== "All" && 
        categoryFilter !== "All") {
      const selectedGroup = categoryGroups.find((g) => g.name === groupFilter);
      if (selectedGroup) {
        const currentCategory = expenseCategories.find((cat) => cat.name === categoryFilter) ||
                                incomeCategories.find((cat) => cat.name === categoryFilter);
        // If current category doesn't belong to the selected group, reset it
        if (!currentCategory || currentCategory.groupId !== selectedGroup.id) {
          categoryFilterManuallyChanged.current = true; // Mark as manually changed to prevent auto-selection
          setCategoryFilter("All");
        }
      }
    }
  }, [groupFilter, categoryFilter, categoryGroups, expenseCategories, incomeCategories, isFullyLoaded, setCategoryFilter]);

  // Auto-select group when category is selected
  // Only runs if user manually changed the category filter AND data is fully loaded
  useEffect(() => {
    if (!isFullyLoaded) return; // Don't auto-select until data is fully loaded
    if (categoryFilterManuallyChanged.current && 
        categoryFilter !== "All" && 
        groupFilter === "All") {
      const transactionCategory = expenseCategories.find((cat) => cat.name === categoryFilter) ||
                                  incomeCategories.find((cat) => cat.name === categoryFilter);
      if (transactionCategory && transactionCategory.groupId) {
        const categoryGroup = categoryGroups.find((g) => g.id === transactionCategory.groupId);
        if (categoryGroup) {
          // Always auto-select the group when a category is selected
          setGroupFilter(categoryGroup.name);
        }
      }
    }
  }, [categoryFilter, groupFilter, expenseCategories, incomeCategories, categoryGroups, isFullyLoaded, setGroupFilter]);

  return {
    handleTypeFilterChange,
    handleGroupFilterChange,
    handleCategoryFilterChange,
  };
};
